/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.0.0-alpha1): datatable.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

import {
  getjQuery,
  typeCheckConfig
} from './util/index'
import Data from './dom/data'
import EventHandler from './dom/event-handler'
import SelectorEngine from './dom/selector-engine'

/**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

const NAME = 'datatable'
const VERSION = '5.0.0-alpha1'
const DATA_KEY = 'bs.datatable'
const EVENT_KEY = `.${DATA_KEY}`
// const DATA_API_KEY = '.data-api'

const SELECTOR_TABLE = 'table'
const SELECTOR_THEAD = 'thead'
const SELECTOR_TH = 'th'
const SELECTOR_TBODY = 'tbody'
const SELECTOR_TFOOT = 'tfoot'
const SELECTOR_PAGE_SIZE = 'select'
const SELECTOR_FILTER = 'input'
const SELECTOR_FORM = '#form'
const SELECTOR_PAGINATION = '#pagination'
const SELECTOR_PAGINATION_LINK = '.page-link'
const SELECTOR_UP_DOWN_BUTTON = '.bi-arrow-up-down'
const SELECTOR_UP_BUTTON = '.bi-arrow-up'
const SELECTOR_DOWN_BUTTON = '.bi-arrow-down'

const Default = {
  pageSize: 5
}

const DefaultType = {
  pageSize: 'number'
}

const EVENT_PAGE_SIZE_CHANGE = `change${EVENT_KEY}`
// const EVENT_PAGE_SIZE_CHANGE_DATA_API = `pageSizeChange${EVENT_KEY}${DATA_API_KEY}`
const EVENT_FILTER_CHANGE = `keyup${EVENT_KEY}`
// const EVENT_FILTER_CHANGE_DATA_API = `keyup${EVENT_KEY}${DATA_API_KEY}`
const EVENT_HEADER_BTN_CLICK = `click${EVENT_KEY}`
// const EVENT_HEADER_BTN__CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`
const EVENT_PAGINATION_LINK_CLICK = `click${EVENT_KEY}`
// const EVENT_PAGINATION_LINK_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`
const ORDER_DOWN = 'desc'
const ORDER_UP = 'asc'

/**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

class Datatable {
  constructor(element, config) {
    this._element = element
    this._config = this._getConfig(config)
    this._sortIndex = null
    this._sortDirection = null

    this.buildTable()
    this.buildForm()
    this.buildPagination()

    Data.setData(element, DATA_KEY, this)
  }

  // Getters

  static get VERSION() {
    return VERSION
  }

  // Public

  stringToArray(str, separator) {
    const separatorRegexp = new RegExp(`\\\\${separator}`, 'g')
    const placeholder = '---'
    const placeholderRegexp = new RegExp(`${placeholder}`, 'g')
    return (str || '').replace(separatorRegexp, placeholder).split(separator).map(item => item.trim().replace(placeholderRegexp, separator))
  }

  buildTable() {
    this._table = SelectorEngine.findOne(SELECTOR_TABLE, this._element)

    this._thead = SelectorEngine.findOne(SELECTOR_THEAD, this._table)
    this.buildHeader()

    this._tbody = SelectorEngine.findOne(SELECTOR_TBODY, this._table)
    this.buildBody()

    this._tfoot = SelectorEngine.findOne(SELECTOR_TFOOT, this._table)
    this.buildFooter()
  }

  buildHeader() {
    this._thead.textContent = ''

    this._headers = this.stringToArray(this._thead.dataset.headers, ',')
    const template = document.createElement('template')
    template.innerHTML = `<tr>${this._headers.map((header, index) => {
      const iconUp = `<svg class="dgfipicon dgfipicon-xxs bi-arrow-up" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"/>
      </svg>`
      const iconDown = `<svg class="dgfipicon dgfipicon-xxs bi-arrow-down" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"/>
      </svg>`
      const iconUpDown = `<svg class="dgfipicon dgfipicon-xxs bi-arrow-up-down" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5zm-7-14a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L4 13.293V1.5a.5.5 0 0 1 .5-.5z"/>
      </svg>`

      const sortIcon = this._sortDirection === ORDER_DOWN ? iconDown : (this._sortDirection === ORDER_UP ? iconUp : '')
      const ariaSort = this._sortDirection === ORDER_DOWN ? 'descending' : (this._sortDirection === ORDER_UP ? 'ascending' : 'none')

      return `<th class="sort-header" aria-sort="${this._sortIndex === index ? ariaSort : 'none'}">${header}
      ${this._sortIndex === index ? sortIcon : iconUpDown}</th>`
    }).join('')}</tr>`
    this._thead.appendChild(template.content.cloneNode(true))

    this._addHeaderListeners()
  }

  buildBody() {
    this._rows = this.stringToArray(this._tbody.dataset.rows, ';').map(row => this.stringToArray(row, ','))

    this._pageSizeOptions = this.stringToArray(this._table.dataset.pageSizeOptions, ',')
      .map(pageSize => Number(pageSize)).filter(pageSize => !Number.isNaN(pageSize))
    this.setPageSize(this._pageSizeOptions[0])

    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
  }

  buildFooter() {
    if (this._tfoot) {
      this._footers = this.stringToArray(this._tfoot.dataset.footers, ',')
      const template = document.createElement('template')
      template.innerHTML = `<tr>${this._footers.map(footer => {
        return `<td>${footer}</td>`
      }).join('')}</tr>`
      this._tfoot.appendChild(template.content.cloneNode(true))
    }
  }

  renderRows(filteredRows) {
    this._tbody.textContent = ''

    const template = document.createElement('template')
    template.innerHTML = `${filteredRows.map(row => {
      return `<tr>${row.map(cell => {
        return `<td>${cell}</td>`
      }).join('')}</tr>`
    }).join('')}`

    this._tbody.appendChild(template.content.cloneNode(true))
  }

  buildForm() {
    const template = document.createElement('template')
    template.innerHTML = `<form id="form" class="row">
      <div class="col-12 col-sm-6">
        <div class="row g-1_4">
          <label for="nombre-elements" class="col-auto col-form-label">Afficher</label>
          <div class="col-12 col-sm-4">
              <select id="nombre-elements" name="nombre" class="form-select">
                  ${this._pageSizeOptions.map((pageSize, index) => {
    return `<option value="${pageSize}" ${index === 0 ? 'selected' : ''}>${pageSize}</option>`
  }).join('')}
                <option value="Tous">Tous</option>
              </select>
          </div>
          <div class="col-auto col-form-label">éléments</div>
        </div>
      </div>
      <div class="col-12 col-sm-6">
        <div class="row g-1_4">
          <label for="saisie-recherche" class="col-auto col-form-label">Rechercher : </label>
          <div class="col-12 col-sm-6">
              <input id="saisie-recherche" type="text" name="recherche" class="form-control" />
          </div>
        </div>
      </div>
    </form>`

    this._element.insertBefore(template.content.cloneNode(true), this._table)
    this._addFormListeners()
  }

  buildPagination() {
    if (this._pagination) {
      this._element.removeChild(this._pagination)
    }

    const template = document.createElement('template')
    template.innerHTML = `<div id="pagination" class="d-flex justify-content-end">
      <nav aria-label="Pagination">
        <ol class="pagination justify-content-center">
          <li class="page-item ${this.hasPreviousPage() ? '' : 'disabled'}">
            <a class="page-link" href="#" aria-label="Première page" aria-disabled="${!this.hasPreviousPage()}">
              <svg id="dgfipicon-page-premiere" class="dgfipicon dgfipicon-xxs" viewBox="0 0 64 64">
                <path d="M30.881 55.601c-.83 0-1.646-.372-2.167-1.093L14.56 35.051a2.689 2.689 0 010-3.149l14.154-19.453c.866-1.207 2.536-1.447 3.733-.591a2.675 2.675 0 01.588 3.74L20.024 33.477l13.011 17.879c.874 1.203.607 2.887-.588 3.74-.47.35-1.023.507-1.566.507z"/>
                <path d="M59.811 36.148h-43.09c-1.478 0-2.672-1.203-2.672-2.672s1.194-2.672 2.672-2.672h43.09c1.478 0 2.672 1.203 2.672 2.672s-1.194 2.672-2.672 2.672zM4.189 60.306a2.66 2.66 0 01-2.672-2.672V9.291a2.677 2.677 0 012.672-2.672 2.675 2.675 0 012.672 2.672v48.343a2.659 2.659 0 01-2.672 2.672z"/>
              </svg>
            </a>
          </li>
          <li class="page-item ${this.hasPreviousPage() ? '' : 'disabled'}">
            <a class="page-link" href="#" aria-label="Page précédente" aria-disabled="${!this.hasPreviousPage()}">
              <svg id="dgfipicon-page-prec&"Éedente" class="dgfipicon dgfipicon-xxs" viewBox="0 0 64 64">
                <path d="M22.019 4.736a2.643 2.643 0 00-1.721 1.065L1.971 30.986c-.077.105-.111.227-.17.341-.052.097-.11.186-.149.29-.099.268-.16.544-.17.826-.001.033-.017.061-.017.094s.016.061.017.094c.01.282.071.559.17.826.039.104.097.192.149.29.059.113.094.235.17.341l18.327 25.185a2.607 2.607 0 002.13 1.082c.539 0 1.08-.157 1.551-.498a2.616 2.616 0 00.584-3.664L9.274 35.17h50.631c1.455 0 2.633-1.185 2.633-2.633s-1.177-2.633-2.633-2.633H9.274L24.563 8.881a2.645 2.645 0 00-.584-3.689 2.638 2.638 0 00-1.96-.456z"/>
              </svg>
            </a>
          </li>
          ${this._pagesIndexes.map((_, index) => {
    return `<li class="page-item ${index === this._pageIndex ? 'active' : ''}">
              <a class="page-link" href="#" aria-label="Page ${index + 1}" aria-current="page" aria-disabled="${index === this._pageIndex}">${index + 1}</a>
            </li>`
  }).join('')}
          <li class="page-item ${this.hasNextPage() ? '' : 'disabled'}">
            <a class="page-link" href="#" aria-label="Page suivante" aria-disabled="${!this.hasNextPage()}">
              <svg id="dgfipicon-page-suivante" class="dgfipicon dgfipicon-xxs" viewBox="0 0 64 64">
                <path d="M41.571 4.702c.812 0 1.617.367 2.131 1.082l18.329 25.185c.669.92.669 2.185 0 3.105L43.702 59.259a2.643 2.643 0 01-3.679.607 2.644 2.644 0 01-.582-3.687l17.204-23.657L39.441 8.864a2.612 2.612 0 01.582-3.661 2.62 2.62 0 011.549-.499z"/>
                <path d="M4.097 29.886h55.806c1.455 0 2.634 1.186 2.634 2.634s-1.178 2.634-2.634 2.634H4.097c-1.455 0-2.634-1.186-2.634-2.634s1.179-2.634 2.634-2.634z"/>
              </svg>
            </a>
          </li>
          <li class="page-item ${this.hasNextPage() ? '' : 'disabled'}">
            <a class="page-link" href="#" aria-label="Dernière page" aria-disabled="${!this.hasNextPage()}">
              <svg id="dgfipicon-page-derniere" class="dgfipicon dgfipicon-xxs" viewBox="0 0 64 64">
                <path d="M33.119 11.325c.83 0 1.646.372 2.167 1.093L49.44 31.875a2.689 2.689 0 010 3.149L35.286 54.477c-.866 1.206-2.536 1.447-3.733.59a2.675 2.675 0 01-.588-3.74l13.011-17.879-13.011-17.879c-.874-1.203-.607-2.887.588-3.74a2.609 2.609 0 011.566-.507z"/>
                <path d="M4.189 30.778h43.09c1.478 0 2.672 1.203 2.672 2.672s-1.194 2.672-2.672 2.672H4.189c-1.478 0-2.672-1.203-2.672-2.672s1.194-2.672 2.672-2.672zM59.812 6.619a2.66 2.66 0 012.672 2.672v48.344a2.677 2.677 0 01-2.672 2.672 2.675 2.675 0 01-2.672-2.672V9.291a2.659 2.659 0 012.672-2.672z"/>
              </svg>
            </a>
          </li>
        </ol>
      </nav>
    </div>`

    this._element.insertBefore(template.content.cloneNode(true), this._table.nextPageSibling)
    this._addPaginationListeners()
  }

  setPageSize(value) {
    const numericValue = Number(value)
    this._pageSize = Number.isNaN(numericValue) ? this._rows.length : numericValue
    this._pagesCount = Math.ceil(this._rows.length / this._pageSize)
    this._pageIndex = 0
    this._pagesIndexes = Array.from(this._pagesCount).fill('')
  }

  filter(event) {
    const { value } = event.target
    if (value === '') {
      this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
      this.sort(this._sortDirection, this._sortIndex)
      return
    }

    this._filteredRows = this._rows.filter(row => {
      return row.find(cell => String(cell).toUpperCase().includes(value.toUpperCase())) !== undefined
    })
    this.sort(this._sortDirection, this._sortIndex)
  }

  reverseDirection(direction) {
    switch (direction) {
      case ORDER_UP:
        return ORDER_DOWN
      case ORDER_DOWN:
        return ORDER_UP
      default:
        return null
    }
  }

  sort(direction, columnIndex) {
    if (direction !== null) {
      const sortedFilteredRows = this._filteredRows.sort((row1, row2) => {
        const cell1 = row1[columnIndex].toUpperCase()
        const cell2 = row2[columnIndex].toUpperCase()
        if (cell1 < cell2) {
          return -1
        }

        if (cell1 > cell2) {
          return 1
        }

        return 0
      })

      switch (direction) {
        case ORDER_UP:
          this._filteredRows = sortedFilteredRows
          break
        case ORDER_DOWN:
          this._filteredRows = sortedFilteredRows.reverse()
          break
        default:
          break
      }
    }

    this.buildHeader()
    this.renderRows(this._filteredRows)
  }

  hasPreviousPage() {
    return this._pageIndex > 0
  }

  hasNextPage() {
    return this._pageIndex < this._pagesCount - 1
  }

  previousPage(event) {
    event.preventDefault()
    this._pageIndex -= 1
    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
    this.buildPagination()
  }

  nextPage(event) {
    event.preventDefault()
    this._pageIndex += 1
    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
    this.buildPagination()
  }

  firstPage(event) {
    event.preventDefault()
    this._pageIndex = 0
    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
    this.buildPagination()
  }

  lastPage(event) {
    event.preventDefault()
    this._pageIndex = this._pagesCount - 1
    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
    this.buildPagination()
  }

  page(event, index) {
    event.preventDefault()
    this._pageIndex = index
    this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
    this.sort(this._sortDirection, this._sortIndex)
    this.buildPagination()
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    config.pageSize = Number(config.pageSize) // Coerce string values
    typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _addFormListeners() {
    this._form = SelectorEngine.findOne(SELECTOR_FORM, this._element)

    this._filterInput = SelectorEngine.findOne(SELECTOR_FILTER, this._form)
    EventHandler.on(this._filterInput, EVENT_FILTER_CHANGE, event => this.filter(event))

    this._pageSizeSelect = SelectorEngine.findOne(SELECTOR_PAGE_SIZE, this._form)
    EventHandler.on(this._pageSizeSelect, EVENT_PAGE_SIZE_CHANGE, event => {
      this.setPageSize(event.target.value)
      this._filteredRows = this._rows.slice(this._pageIndex * this._pageSize, (this._pageIndex + 1) * this._pageSize)
      this.sort(this._sortDirection, this._sortIndex)
      this.buildPagination()
    })
  }

  _addHeaderListeners() {
    const ths = SelectorEngine.find(SELECTOR_TH, this._thead)
    ths.forEach((th, index) => {
      const upDownBtn = SelectorEngine.findOne(SELECTOR_UP_DOWN_BUTTON, th)
      if (upDownBtn) {
        EventHandler.on(th, EVENT_HEADER_BTN_CLICK, _ => {
          this._sortDirection = this.reverseDirection(ORDER_DOWN)
          this._sortIndex = index
          this.sort(this._sortDirection, this._sortIndex)
        })
      }

      const upBtn = SelectorEngine.findOne(SELECTOR_UP_BUTTON, th)
      if (upBtn) {
        EventHandler.on(th, EVENT_HEADER_BTN_CLICK, _ => {
          this._sortDirection = this.reverseDirection(ORDER_UP)
          this._sortIndex = index
          this.sort(this._sortDirection, this._sortIndex)
        })
      }

      const downBtn = SelectorEngine.findOne(SELECTOR_DOWN_BUTTON, th)
      if (downBtn) {
        EventHandler.on(th, EVENT_HEADER_BTN_CLICK, _ => {
          this._sortDirection = this.reverseDirection(ORDER_DOWN)
          this._sortIndex = index
          this.sort(this._sortDirection, this._sortIndex)
        })
      }
    })
  }

  _addPaginationListeners() {
    this._pagination = SelectorEngine.findOne(SELECTOR_PAGINATION, this._element)

    const paginationLinks = SelectorEngine.find(SELECTOR_PAGINATION_LINK, this._pagination)
    paginationLinks.forEach((link, index) => {
      switch (index) {
        case 0:
          EventHandler.on(link, EVENT_PAGINATION_LINK_CLICK, event => this.firstPage(event))
          break
        case 1:
          EventHandler.on(link, EVENT_PAGINATION_LINK_CLICK, event => this.previousPage(event))
          break
        case paginationLinks.length - 2:
          EventHandler.on(link, EVENT_PAGINATION_LINK_CLICK, event => this.nextPage(event))
          break
        case paginationLinks.length - 1:
          EventHandler.on(link, EVENT_PAGINATION_LINK_CLICK, event => this.lastPage(event))
          break
        default:
          EventHandler.on(link, EVENT_PAGINATION_LINK_CLICK, event => this.page(event, index - 2))
      }
    })
  }

  // Static

  static jQueryInterface(config) {
    return this.each(function () {
      let data = Data.getData(this, DATA_KEY)

      if (!data) {
        data = new Datatable(this)
      }

      if (config === 'show') {
        data[config]()
      }
    })
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY)
  }
}

const $ = getjQuery()

/**
   * ------------------------------------------------------------------------
   * jQuery
   * ------------------------------------------------------------------------
   * add .datatable to jQuery only if jQuery is present
   */
/* istanbul ignore if */
if ($) {
  const JQUERY_NO_CONFLICT = $.fn[NAME]
  $.fn[NAME] = Datatable.jQueryInterface
  $.fn[NAME].Constructor = Datatable
  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return Datatable.jQueryInterface
  }
}

export default Datatable
